import {
  GINJA_PLATFORM_API_BASE,
  SSO_COOKIE_SENTINEL,
  GINJA_LOCAL_STORAGE_KEYS,
  getEnvBearerToken,
} from "./ginjaAuth";

export const GINJA_BRAND_STORAGE_KEY = "ginja-brand-settings";
export const GINJA_BRAND_UPDATED_EVENT = "ginja-brand-updated";

function hexToHsl(hex) {
  const h = (hex || "").replace("#", "").trim();
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const light = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      default:
        hue = (r - g) / d + 4;
        break;
    }
    hue /= 6;
  }

  return {
    h: Math.round(hue * 360),
    s: Math.round(sat * 100),
    l: Math.round(light * 100),
  };
}

export function bearerForGinjaApis() {
  const token = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.token);
  if (token && token !== SSO_COOKIE_SENTINEL) return token;

  const authToken = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.authToken);
  if (authToken && authToken !== SSO_COOKIE_SENTINEL) return authToken;

  return getEnvBearerToken();
}

export async function fetchBrandSettings() {
  const bearer = bearerForGinjaApis();
  const headers = {};
  if (bearer) headers.Authorization = `Bearer ${bearer}`;

  const res = await fetch(`${GINJA_PLATFORM_API_BASE}/brand-settings`, {
    method: "GET",
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GET /brand-settings failed (${res.status}): ${text}`);
  }
  return await res.json();
}

function setFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

export function applyBrandSettings(data) {
  if (!data) return;

  try {
    localStorage.setItem(GINJA_BRAND_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }

  const brandName = data?.brand_name || "Case Management";
  document.title = brandName;
  if (data?.favicon_url) setFavicon(data.favicon_url);

  const root = document.documentElement;
  const primary = hexToHsl(data?.primary_color);
  const secondary = hexToHsl(data?.secondary_color);
  const accent = hexToHsl(data?.accent_color);

  if (primary) root.style.setProperty("--primary", `${primary.h} ${primary.s}% ${primary.l}%`);
  if (secondary) root.style.setProperty("--secondary", `${secondary.h} ${secondary.s}% ${secondary.l}%`);
  if (accent) root.style.setProperty("--accent", `${accent.h} ${accent.s}% ${accent.l}%`);

  window.dispatchEvent(new Event(GINJA_BRAND_UPDATED_EVENT));
}

export function applyCachedBrandSettings() {
  const raw = localStorage.getItem(GINJA_BRAND_STORAGE_KEY);
  if (!raw) return;
  try {
    applyBrandSettings(JSON.parse(raw));
  } catch {
    // ignore
  }
}

export function resetBrandToDefaults() {
  localStorage.removeItem(GINJA_BRAND_STORAGE_KEY);
  document.title = "Case Management";

  const root = document.documentElement;
  root.style.removeProperty("--primary");
  root.style.removeProperty("--secondary");
  root.style.removeProperty("--accent");

  window.dispatchEvent(new Event(GINJA_BRAND_UPDATED_EVENT));
}

